import React, { useEffect, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DashboardWidget } from '../../types/Dashboard';
import WidgetContainer from './WidgetContainer';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardGridProps {
  widgets: DashboardWidget[];
  onLayoutChange: (layout: any) => void;
  onRemoveWidget: (widgetId: string) => void;
  onWidgetConfigChange: (widgetId: string, config: Record<string, any>) => void;
  isEditing: boolean;
  children?: React.ReactNode;
}

const DashboardGrid: React.FC<DashboardGridProps> = ({
  widgets,
  onLayoutChange,
  onRemoveWidget,
  onWidgetConfigChange,
  isEditing
}) => {
  const gridLayoutRef = useRef<any>(null);
  // Mapeamento de largura: small = 4 colunas (33%), medium = 8 colunas (66%), large = 12 colunas (100%)
  const getWidthFromSize = (size: string): number => {
    return size === 'small' ? 4 : size === 'medium' ? 8 : 12;
  };

  const getSizeFromWidth = (width: number): 'small' | 'medium' | 'large' => {
    // Arredonda para o tamanho mais próximo: 4 (33%), 8 (66%) ou 12 (100%)
    if (width <= 4) return 'small';
    if (width <= 8) return 'medium';
    return 'large';
  };

  // Mapeamento de altura: apenas 2 valores padrão (base e o dobro) para manter grade uniforme
  // Altura base: 3 linhas, Altura dobro: 6 linhas
  const BASE_HEIGHT = 3;
  const DOUBLE_HEIGHT = 6;
  const STANDARD_HEIGHTS = [BASE_HEIGHT, DOUBLE_HEIGHT];
  
  const getHeightFromSize = (size: string): number => {
    // Altura padrão baseada no tamanho: small e medium = base (3), large = dobro (6)
    return size === 'large' ? DOUBLE_HEIGHT : BASE_HEIGHT;
  };

  const getNormalizedHeight = (height: number): number => {
    // Arredonda para o valor padrão mais próximo (3 ou 6 linhas)
    // Se estiver mais próximo de 3, retorna 3; caso contrário, retorna 6
    const midpoint = (BASE_HEIGHT + DOUBLE_HEIGHT) / 2; // 4.5
    return height <= midpoint ? BASE_HEIGHT : DOUBLE_HEIGHT;
  };

  const layouts = {
    lg: widgets.map(widget => ({
      i: widget.id,
      x: widget.position.x,
      y: widget.position.y,
      w: getWidthFromSize(widget.size),
      h: getHeightFromSize(widget.size),
      minW: 4,
      maxW: 12,
      minH: BASE_HEIGHT,
      maxH: DOUBLE_HEIGHT
    }))
  };

  const handleLayoutChange = (layout: any, layouts: any) => {
    // Usa o layout fornecido diretamente (já compactado pelo react-grid-layout)
    const updatedWidgets = widgets.map(widget => {
      const layoutItem = layout.find((item: any) => item.i === widget.id) || 
                        layouts.lg.find((item: any) => item.i === widget.id);
      if (layoutItem) {
        // Normaliza a largura para um dos valores permitidos (4, 8 ou 12)
        const normalizedSize = getSizeFromWidth(layoutItem.w);
        const normalizedWidth = getWidthFromSize(normalizedSize);
        // Normaliza a altura para um dos valores padrão
        const normalizedHeight = getNormalizedHeight(layoutItem.h);
        
        return {
          ...widget,
          position: { x: layoutItem.x, y: layoutItem.y },
          size: normalizedSize
        };
      }
      return widget;
    });
    onLayoutChange(updatedWidgets);
  };

  const handleResize = (layout: any, oldItem: any, newItem: any, placeholder: any, e: any, element: any) => {
    // Faz snap em tempo real para os valores permitidos durante o redimensionamento
    const normalizedSize = getSizeFromWidth(newItem.w);
    const normalizedWidth = getWidthFromSize(normalizedSize);
    const normalizedHeight = getNormalizedHeight(newItem.h);
    
    // Atualiza diretamente os objetos (passados por referência)
    if (placeholder) {
      placeholder.w = normalizedWidth;
      placeholder.h = normalizedHeight;
    }
    newItem.w = normalizedWidth;
    newItem.h = normalizedHeight;
    
    // Atualiza o layout também
    const itemIndex = layout.findIndex((item: any) => item.i === newItem.i);
    if (itemIndex !== -1) {
      layout[itemIndex].w = normalizedWidth;
      layout[itemIndex].h = normalizedHeight;
    }
  };

  const handleResizeStop = (layout: any, oldItem: any, newItem: any, placeholder: any, e: any, element: any) => {
    // Garante que após parar o redimensionamento, os valores estão normalizados
    const normalizedSize = getSizeFromWidth(newItem.w);
    const normalizedWidth = getWidthFromSize(normalizedSize);
    const normalizedHeight = getNormalizedHeight(newItem.h);
    
    // Atualiza o layout com largura e altura normalizadas
    const updatedLayout = layout.map((item: any) => {
      if (item.i === newItem.i) {
        return { ...item, w: normalizedWidth, h: normalizedHeight };
      }
      return item;
    });

    // Cria layouts atualizados e chama handleLayoutChange
    const updatedLayouts = { lg: updatedLayout };
    handleLayoutChange(updatedLayout, updatedLayouts);
  };

  // Força compactação quando widgets são removidos ou tela é redimensionada
  useEffect(() => {
    const handleResize = () => {
      if (gridLayoutRef.current) {
        // Força atualização do layout ao redimensionar a janela
        try {
          gridLayoutRef.current.onWindowResize();
        } catch (error) {
          // Ignora erros se o método não estiver disponível
        }
      }
    };

    // Debounce para evitar muitas chamadas durante o resize
    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Força compactação quando a lista de widgets muda (widget removido)
  useEffect(() => {
    if (gridLayoutRef.current) {
      // Delay para garantir que o DOM foi atualizado após remoção
      const timeoutId = setTimeout(() => {
        if (gridLayoutRef.current) {
          try {
            // Força recálculo do layout - o react-grid-layout fará a compactação automaticamente
            // devido ao compactType="vertical"
            gridLayoutRef.current.onWindowResize();
          } catch (error) {
            // Ignora erros se o método não estiver disponível
          }
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [widgets.length, widgets.map(w => w.id).join(',')]);

  return (
    <div className="min-h-screen">
      <ResponsiveGridLayout
        ref={gridLayoutRef}
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={90}
        isDraggable={isEditing}
        isResizable={isEditing}
        onLayoutChange={handleLayoutChange}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        compactType="vertical"
        preventCollision={!isEditing}
        allowOverlap={false}
        draggableCancel=".widget-button, .widget-controls, .widget-header, button, input, select, textarea, a"
        useCSSTransforms={true}
      >
        {widgets.map(widget => (
          <div key={widget.id}>
            <WidgetContainer
              widget={widget}
              onRemove={onRemoveWidget}
              onConfigChange={onWidgetConfigChange}
              isEditing={isEditing}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
};

export default DashboardGrid;

